import React, { useEffect, useState } from 'react';
import { Box, Button, MenuItem, Stack, Tab, Tabs, TextField, Typography } from '@mui/material';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';

import { useStorytellerConcepts, useStorytellerConcept } from '../../../queries';
import type { StoryOutlineSection } from '../../../domain';
import { ConceptEditorModal } from './ConceptEditorModal';
import { OutlineView } from './OutlineView';

interface Props {
  studioId: string;
  activeConceptId: string | null;
  onActiveConceptChange: (id: string | null) => void;
  activeSectionId?: string;
  onSelectSection: (section: StoryOutlineSection) => void;
}

type RailTab = 'concept' | 'outline';

/**
 * Storyteller sub-surface (W-F10): concept picker + Concept/Outline tabs. The
 * headline flow — Concept editor → generated Outline → pick a section to draft —
 * lives here; section selection bubbles up to the WriterLab shell, which threads
 * `sectionId` into Generate and seeds the prompt with the section synopsis.
 */
export const StorytellerRail: React.FC<Props> = ({
  studioId,
  activeConceptId,
  onActiveConceptChange,
  activeSectionId,
  onSelectSection,
}) => {
  const { data: concepts } = useStorytellerConcepts();
  const { data: detail } = useStorytellerConcept(activeConceptId ?? undefined);
  const [tab, setTab] = useState<RailTab>('concept');
  const [modal, setModal] = useState<{ open: boolean; editing: boolean }>({
    open: false,
    editing: false,
  });

  // Default to the most-recent concept once the list loads.
  useEffect(() => {
    if (!activeConceptId && concepts && concepts.length > 0) {
      onActiveConceptChange(concepts[0].id);
    }
  }, [activeConceptId, concepts, onActiveConceptChange]);

  const handleSaved = (conceptId: string) => {
    onActiveConceptChange(conceptId);
    setTab('outline');
  };

  return (
    <Box sx={{ borderBottom: theme => `1px solid ${theme.palette.divider}` }} aria-label="Storyteller panel">
      <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 2, pt: 1.5 }}>
        <AutoStoriesIcon fontSize="small" sx={{ opacity: 0.7 }} />
        <Typography variant="overline" color="text.secondary">
          Storyteller
        </Typography>
      </Stack>

      <Tabs value={tab} onChange={(_, v) => setTab(v as RailTab)} variant="fullWidth" sx={{ minHeight: 36 }}>
        <Tab value="concept" label="Concept" sx={{ minHeight: 36, fontSize: '0.8rem' }} />
        <Tab value="outline" label="Outline" sx={{ minHeight: 36, fontSize: '0.8rem' }} />
      </Tabs>

      {tab === 'concept' && (
        <Box sx={{ p: 2 }}>
          {concepts && concepts.length > 0 && (
            <TextField
              select
              label="Concept"
              value={activeConceptId ?? ''}
              onChange={e => onActiveConceptChange(e.target.value || null)}
              size="small"
              fullWidth
              sx={{ mb: 1.5 }}
            >
              {concepts.map(c => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </TextField>
          )}

          {detail ? (
            <Stack spacing={1}>
              <Typography variant="subtitle2">{detail.concept.name}</Typography>
              <Typography variant="caption" color="text.secondary">
                {detail.concept.premise}
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button size="small" startIcon={<EditIcon />} onClick={() => setModal({ open: true, editing: true })}>
                  Edit
                </Button>
                <Button size="small" startIcon={<AddIcon />} onClick={() => setModal({ open: true, editing: false })}>
                  New
                </Button>
              </Stack>
            </Stack>
          ) : (
            <Stack spacing={1} alignItems="flex-start">
              <Typography variant="body2" color="text.secondary">
                Start with a Concept — premise, characters, setting — then generate a chapter outline.
              </Typography>
              <Button
                variant="contained"
                startIcon={<AutoStoriesIcon />}
                onClick={() => setModal({ open: true, editing: false })}
              >
                Start with a Concept
              </Button>
            </Stack>
          )}
        </Box>
      )}

      {tab === 'outline' && (
        <Box>
          {activeConceptId ? (
            <OutlineView
              conceptId={activeConceptId}
              activeSectionId={activeSectionId}
              onSelectSection={onSelectSection}
            />
          ) : (
            <Box sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Create a concept first to generate an outline.
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {modal.open && (
        <ConceptEditorModal
          open
          studioId={studioId}
          initialConcept={modal.editing ? detail?.concept : undefined}
          onClose={() => setModal({ open: false, editing: false })}
          onSaved={handleSaved}
        />
      )}
    </Box>
  );
};
